import React, { useEffect, useMemo, useState } from 'react';
import { Button, logout, selectOidcUserLogged, selectUserProfileId } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { makeStyles, Menu, Theme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import PersonIcon from '@material-ui/icons/Person';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { selectedAccStateSelector } from '@core/store/library-stores/selectors';
import { useUserDetailsDataSource } from './userDetailsDataSource';
import { environment } from '@root/environment';
import { useSelectedSitesDataSource } from '../Sidebar/SiteList/sitesDataSource';
import { accountsReducer } from '@core/store/library-stores/accounts';

export const useStyles = makeStyles((theme: Theme) => ({
  userDetailsMenuWrapper: {
    top: '66px !important',
    left: 'unset !important',
    right: 8,
  },
  version: {
    fontSize: '12px',
    textAlign: 'right',
    width: '100%',
    color: '#B6BCCE',
    borderTop: '1px solid #B6BCCE',
    paddingTop: '7px',
    marginTop: '13px',
  },
}));

export const UserDetails: React.FC = () => {
  const classes = useStyles();

  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { userMainAccount } = useAppSelector(selectedAccStateSelector);

  const userId: number = useAppSelector(selectUserProfileId);
  const { name }: { name: string } = useAppSelector(selectOidcUserLogged) ?? {
    name: '',
  };

  const { userAccessProfile, getUserAccessProfile, loading, error } = useUserDetailsDataSource();

  const { handleLoadSelectedList } = useSelectedSitesDataSource();

  useEffect(() => {
    if (userId) {
      getUserAccessProfile(userId.toString());
    }
  }, [userId, getUserAccessProfile]);

  const userRole = useMemo(
    () =>
      userAccessProfile
        ? userAccessProfile?.roles?.find((role) => role.applicationName === environment.appName)?.roleName || null
        : '',
    [userAccessProfile]
  );

  useEffect(() => {
    if (userAccessProfile && !loading && userId && !error) {
      dispatch(accountsReducer.slice.actions.setUserRole(userRole));
    }
  }, [dispatch, userAccessProfile, userId, userRole, loading, error]);

  return (
    <>
      <Button
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        variant="outlined"
        style={{ fontSize: 16 }}
        endIcon={Boolean(anchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        startIcon={<PersonIcon />}
        dataTestId="layout-navbar-buttons-user-button"
      >
        {name}
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{
          paper: `${classes.userDetailsMenuWrapper} px-5 py-3`,
          list: 'flex items-center flex-col text-grey font-semibold',
        }}
        data-testid="layout-navbar-buttons-user-menu"
      >
        <div className="mb-2 text-base" data-testid="layout-navbar-buttons-user-name">
          <Trans>Parent Account</Trans>: {userMainAccount?.name}
        </div>
        <div className="mb-2 text-base" data-testid="layout-navbar-buttons-user-role">
          <Trans>User Level</Trans>: {loading ? '' : userRole}
        </div>
        <Button
          variant="outlined"
          onClick={() => {
            handleLoadSelectedList(
              null,
              () =>
                logout({
                  sessionCookieDomain: environment.sessionCookieDomain,
                  sessionCookieIsSecure: environment.sessionCookieSecure,
                  sessionIdleTimeout: environment.sessionCookieIdleTimeout,
                }),
              true
            );
          }}
          style={{ fontSize: '16px' }}
          data-testid="layout-navbar-buttons-user-logout-button"
        >
          <Trans>Log out</Trans>
        </Button>
        <div className={classes.version} data-testid="layout-navbar-buttons-user-version">
          <Trans comment="Application version">Version</Trans>: {environment.version}
        </div>
      </Menu>
    </>
  );
};
